/**
 * Email one-click ticket self-check-in. Rendered by /self-checkin when the URL carries a
 * ?code=<TK code> (the link the ticket email includes via {{checkin_url}}). The ticket is
 * subject-bound, so:
 *   - a MEMBER is checked in automatically on load, with a confirmation + upgrade-offer note;
 *   - a NON-MEMBER must confirm the USLS disclaimers before check-in completes.
 * Mobile-first, standalone (no auth).
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import MobileCheckinShell from "./MobileCheckinShell";
import {
  ticketLinkResolve,
  ticketLinkCheckin,
  type TicketLinkResolution,
  type TicketLinkCheckinResult,
} from "../../services/selfCheckin.service";
import { getTicketRewardStatus } from "../../services/redemption.service";

// TODO(business copy): final USLS disclaimer wording to be supplied by the business team.
// Exported as the single source of truth — the walk-in guest self-check-in (#1d) reuses this wording.
export const USLS_DISCLAIMER =
  "By checking in, I acknowledge and agree to the U.S. LawShield terms, conditions, and event disclaimers, and consent to be contacted about U.S. LawShield membership and related offers.";

type Phase =
  | "loading"
  | "disclaimer"
  | "checking"
  | "done"
  | "notopen"
  | "error";

const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <MobileCheckinShell title="Event check-in">
    <div className="rounded-2xl bg-white p-6 shadow-sm">{children}</div>
  </MobileCheckinShell>
);

function rewardLine(
  rewardState?: string | null,
  rewardName?: string | null,
): string | null {
  // 2026-09-08 owner rule: a check-in ticket is not a voucher until an operator upgrades it, so
  // never name or value the reward here. Anything short of 'assigned' promises nothing at all --
  // the caller already renders "if a reward applies, it will be finalized for you".
  if (rewardState === "assigned") {
    return "You're all set for your event reward.";
  }
  return null;
}

const TicketLinkCheckin: React.FC<{ code: string }> = ({ code }) => {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>("loading");
  const [resolved, setResolved] = useState<TicketLinkResolution | null>(null);
  const [result, setResult] = useState<TicketLinkCheckinResult | null>(null);
  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState<string>("");
  const [redirecting, setRedirecting] = useState(false);
  const ran = useRef(false);

  const doCheckin = useCallback(
    async (disclaimerAck: boolean) => {
      setPhase("checking");
      const r = await ticketLinkCheckin(code, disclaimerAck);
      if (r.checkedIn) {
        setResult(r);
        setPhase("done");
      } else if (r.needsDisclaimer) {
        setPhase("disclaimer");
      } else if (r.status === 409) {
        setError(r.error || "Check-in is not open for this event yet.");
        setPhase("notopen");
      } else {
        setError(
          r.error ||
            "We couldn't complete your check-in. Please see a USLS representative.",
        );
        setPhase("error");
      }
    },
    [code],
  );

  // Once checked in (fresh OR already), if the ticket has been UPGRADED TO A REDEEMABLE VOUCHER, hand
  // the member straight to the redemption page — not the "checked in" confirmation. Only the precise
  // `status==='redeemable'` (checked in + reward assigned + within window + unredeemed) qualifies;
  // a pending/no-reward ticket stays on the confirmation. rewardState from resolve is only
  // 'assigned'/'pending', so we fetch the exact status here.
  useEffect(() => {
    if (phase !== "done" || !result?.checkedIn) {
      return;
    }
    let cancelled = false;
    (async () => {
      const rs = await getTicketRewardStatus(code).catch(() => null);
      if (!cancelled && rs?.found && rs.status === "redeemable") {
        setRedirecting(true);
        navigate(`/redeem?code=${encodeURIComponent(code.toUpperCase())}`, {
          replace: true,
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [phase, result?.checkedIn, code, navigate]);

  useEffect(() => {
    if (ran.current) {
      return;
    }
    ran.current = true;
    (async () => {
      const r = await ticketLinkResolve(code);
      setResolved(r);
      if (!r.found || r.status === 404) {
        setError(r.error || "That ticket code was not recognized.");
        setPhase("error");
        return;
      }
      if (r.status >= 500 || r.status === 429) {
        setError(
          r.error || "Something went wrong. Please try again in a moment.",
        );
        setPhase("error");
        return;
      }
      if (r.alreadyCheckedIn) {
        // Already in — show the confirmation (with reward status) without re-admitting.
        setResult({
          status: 200,
          checkedIn: true,
          alreadyCheckedIn: true,
          isMember: r.isMember,
          eventTitle: r.eventTitle,
          firstName: r.firstName,
          rewardName: r.rewardName,
          rewardState: r.rewardState,
        });
        setPhase("done");
        return;
      }
      if (!r.checkinOpen) {
        setError(
          "Check-in for this event isn't open yet — please use this link on the event day.",
        );
        setPhase("notopen");
        return;
      }
      if (r.needsDisclaimer) {
        setPhase("disclaimer");
        return;
      }
      // Member → auto check-in.
      await doCheckin(false);
    })();
  }, [code, doCheckin]);

  // ---- LOADING / CHECKING ----
  if (phase === "loading" || phase === "checking") {
    return (
      <Shell>
        <p className="py-8 text-center text-gray-500">
          {phase === "loading" ? "Looking up your ticket…" : "Checking you in…"}
        </p>
      </Shell>
    );
  }

  // ---- DISCLAIMER (non-member) ----
  if (phase === "disclaimer") {
    return (
      <Shell>
        <h2 className="text-lg font-semibold text-gray-900">
          {resolved?.eventTitle || "Your event"}
        </h2>
        <p className="mt-1 text-sm text-gray-600">
          {resolved?.firstName ? `Hi ${resolved.firstName}! ` : ""}One quick
          step before we check you in.
        </p>
        <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs leading-relaxed text-gray-700">
          {USLS_DISCLAIMER}
        </div>
        <label className="mt-4 flex items-start gap-3">
          <input
            type="checkbox"
            className="mt-0.5 h-5 w-5"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
          />
          <span className="text-sm text-gray-900">
            I have read and agree to the disclaimers above.
          </span>
        </label>
        <button
          type="button"
          disabled={!agreed}
          onClick={() => void doCheckin(true)}
          className="mt-5 w-full rounded-lg bg-[#A02428] px-4 py-3 text-base font-semibold text-white hover:bg-[#8a1f22] disabled:opacity-50"
        >
          Complete check-in
        </button>
      </Shell>
    );
  }

  // ---- NOT OPEN YET ----
  if (phase === "notopen") {
    return (
      <Shell>
        <h2 className="text-lg font-semibold text-gray-900">
          {resolved?.eventTitle || "Your event"}
        </h2>
        <p className="mt-3 text-sm text-gray-700">{error}</p>
      </Shell>
    );
  }

  // ---- ERROR ----
  if (phase === "error") {
    return (
      <Shell>
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      </Shell>
    );
  }

  // ---- DONE ----
  // Upgraded-to-voucher tickets are being handed off to /redeem — show a brief transition, not the
  // confirmation, to avoid flashing the wrong screen.
  if (redirecting) {
    return (
      <Shell>
        <p className="text-center text-sm text-gray-600">
          Taking you to your reward…
        </p>
      </Shell>
    );
  }
  const rl = rewardLine(result?.rewardState, result?.rewardName);
  return (
    <Shell>
      <div className="rounded-lg border border-green-200 bg-green-50 p-5 text-center">
        <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-green-600 text-2xl text-white">
          ✓
        </div>
        <p className="text-lg font-bold text-green-900">
          {result?.alreadyCheckedIn
            ? "You're already checked in!"
            : "You're checked in!"}
        </p>
        <p className="mt-1 text-sm text-green-800">
          {result?.firstName ? `${result.firstName}, ` : ""}
          {resolved?.eventTitle || "your event"}.
        </p>
        {rl && <p className="mt-3 text-sm font-medium text-green-900">{rl}</p>}
      </div>
      {/* Audit H3: only tell the holder to CLAIM when the reward is actually assigned — a
          still-pending placeholder redeems $0 and must not be presented as claimable. */}
      {result?.rewardState === "assigned" ? (
        <p className="mt-4 text-center text-xs font-medium text-green-800">
          Keep this screen open to show at entry.
        </p>
      ) : (
        <p className="mt-4 text-center text-xs text-gray-500">
          You're all set. If a reward applies, it will be finalized for you.
        </p>
      )}
    </Shell>
  );
};

export default TicketLinkCheckin;
